import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  TableBody,
  Snackbar,
  Alert,
} from "@mui/material";
import { calculateTherapyDuration } from "../utils/utils";
import CheckIcon from "@mui/icons-material/Check";
import PrintIcon from "@mui/icons-material/Print";
import SendIcon from "@mui/icons-material/Send";
import { ArrowBack } from "@mui/icons-material";
import Logo from "../assets/ekologo.png";
import { calculateAndReturnParams, calculateBMI } from "../utils/utils";
import { addMonthsAndAdjustWeekend, formatDates } from "../utils/addMonths";
import { DynamicImage } from "./DynamicImage";
import { useLocation, useNavigate } from "react-router-dom";
import { determineClosestStepen } from "../utils/determineClosestStepen";
import { useEditPatientData } from "../api/ekoApi";

const Wrapper = styled.div`
  background: none;
  padding: 10px 20px 20px 10px;
  margin: 0px;
`;

const NoBorderCell = styled(TableCell)`
  &&&.MuiTableCell-root {
    border: none;
    padding: 2px 1px;
  }
`;

const BoldWrapper = styled.span`
  font-weight: bold;
`;

const StyledMidTitle = styled(Box)`
  border-top: 1px solid #006633;
  border-bottom: 1px solid #006633;
  text-align: center;
  color: #006633;
`;

const StyledMainTitle = styled(Box)`
  border-top: 2px solid #006633;
  border-bottom: 2px solid #006633;
  text-align: center;
  padding: 5px 0px;
  color: #006633;
  width: 100%;
`;

const PrintButton = styled(Button)`
  @media print {
    display: none; /* Hide the button when printing */
  }
`;

const Report = () => {
  const { t } = useTranslation();
  const [serialNumber, setSerialNumber] = useState("");
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const editSerialNumber = useEditPatientData();
  const navigate = useNavigate();

  const { personalData } = location?.state || {};

  const visinasvodaYL = (personalData?.mlaDepthLeft * 0.4 ).toFixed(2)  
  const visinasvodaYR = (personalData?.mlaDepthRight * 0.4 ).toFixed(2) 

  const optimalnaDubinaLevo = ((2 / 3) * personalData?.footWidthLeft).toFixed(
    2
  );
  const optimalnaDubinaDesno = ((2 / 3) * personalData?.footWidthRight).toFixed(
    2
  );
  // todo x * 0.4 = visinasvoda y
  const visinaSvodaLevo = Math.round(optimalnaDubinaLevo * 0.4).toFixed(2);
  const visinaSvodaDesno = Math.round((optimalnaDubinaDesno * 0.4).toFixed(2));
  const fdxL = Math.round(optimalnaDubinaLevo - personalData?.mlaDepthLeft);
  const fdxD = Math.round(optimalnaDubinaDesno - personalData?.mlaDepthRight);
  const fdyL = Math.round(visinaSvodaLevo - personalData?.mlaDepthLeft * 0.4);
  const fdyD = Math.round(visinaSvodaDesno - personalData?.mlaDepthRight * 0.4);
  const procenatlevo = Math.round((fdyL / visinaSvodaLevo) * 100);
  //const procenatlevonoround = (fdyL / visinaSvodaLevo) * 100;
  const procenatDesno = Math.round((fdyD / visinaSvodaDesno) * 100);
  //const stepenLevo = Math.round((fdxL / optimalnaDubinaLevo) * 100)
  //const stepenDesno = Math.round((fdxD / optimalnaDubinaDesno) * 100)
  const stepenLevoNoRound = (fdxL / optimalnaDubinaLevo) * 100;
  const stepenDesnoNoRound = (fdxD / optimalnaDubinaDesno) * 100;
  const mlaEvelLeft = determineClosestStepen(stepenLevoNoRound);
  const mlaEvelRight = determineClosestStepen(stepenDesnoNoRound);
  const leftImage = calculateAndReturnParams(
    mlaEvelLeft?.percentage,
    procenatlevo,
    "levo",
    personalData?.leftFootprintSplit,
    mlaEvelLeft?.stepen
  );
  const rightImage = calculateAndReturnParams(
    mlaEvelRight?.percentage,
    procenatDesno,
    "desno",
    personalData?.rightFootprintSplit,
    mlaEvelRight?.stepen
  );

  const handleSendData = () => {
    editSerialNumber.mutate(
      { id: personalData._id, dataToEdit: { serialNumber: serialNumber } },
      {
        onSuccess: () => {
          setOpen(true);
          setSerialNumber("");
        },
      }
    );
  };

  function handlePrint() {
    window.print();
  }
  const handleBack = () => {
    navigate(-1);
  };

  const check1 = new Date((addMonthsAndAdjustWeekend(personalData?.examinationdate, 2)))
  const check2 = new Date((addMonthsAndAdjustWeekend(personalData?.examinationdate, 4)))
  const check3 = new Date((addMonthsAndAdjustWeekend(personalData?.examinationdate, 6)))

  return (
    <Wrapper>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
          {t("serialNumberSucces")}
        </Alert>
      </Snackbar>
      <TableContainer component={Paper} elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "5px",
            marginBottom: "10px",
            marginRight: "15px",
            "@media print": {
              display: "none", // Hide during print
            },
          }}
        >
          <Box>
            <Button startIcon={<ArrowBack />} onClick={handleBack} size="small">
              {t("back")}
            </Button>
          </Box>
          <Box>
            <TextField
              label={t("serialNumber")}
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              //variant="outlined"
              size="small"
              style={{ marginRight: "10px" }}
            />
            <Button
              style={{ marginRight: "10px" }}
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleSendData}
              size="small"
            >
              {t("updateSerialNumber")}
            </Button>
            <PrintButton
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              size="small"
            >
              Print
            </PrintButton>
          </Box>
        </Box>
        <Box align="center" sx={{ marginBottom: "10px" }}>
          <img src={Logo} width="240" height="auto" alt="logoinreport" />
        </Box>
        <StyledMainTitle>
          <Typography>
            <strong>{t("ekovelReport")}</strong>
          </Typography>
        </StyledMainTitle>
        <Typography variant="body2" align="center" m={2}>
          {t("date")}:{" "}
          <strong>{formatDates(personalData?.examinationdate)}</strong>
        </Typography>

        <Table
          size="small"
          sx={{ maxWidth: 400, marginBottom: 1 }}
          align="center"
        >
          <TableBody>
            <TableRow>
              <NoBorderCell align="left">
                <BoldWrapper>{t("phoneNumber")}: </BoldWrapper>
                {personalData?.phoneNumber1}
              </NoBorderCell>
              <NoBorderCell align="left">
                <BoldWrapper>Email:</BoldWrapper>
                {personalData?.email}
              </NoBorderCell>
            </TableRow>
            <TableRow>
              <NoBorderCell align="left">
                <BoldWrapper>{t("nameParam")}: </BoldWrapper>{" "}
                {personalData?.firstName} {personalData?.lastName}
              </NoBorderCell>
              {/* <NoBorderCell align="left">
                <BoldWrapper>Adress:</BoldWrapper> Kej oslobodjenja 67
              </NoBorderCell> */}
            </TableRow>
            <TableRow>
              <NoBorderCell align="left">
                <BoldWrapper>{t("dateofBirth")}: </BoldWrapper>
                {formatDates(personalData?.birthdate)}
              </NoBorderCell>
            </TableRow>
          </TableBody>
        </Table>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <DynamicImage imageName={leftImage} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <StyledMidTitle>
              <Typography>
                <strong>{t("generalExamination").toUpperCase()}</strong>
              </Typography>
            </StyledMidTitle>
            <Table
              size="small"
              sx={{ maxWidth: 400, marginBottom: 1 }}
              align="center"
            >
              <TableBody>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{t("height")} [cm]</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    {personalData?.height}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{t("weight")} [kg]</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    {personalData?.weight}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{t("optimalWeight")} [kg]</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    {calculateBMI(personalData?.height, personalData?.gender)}
                  </NoBorderCell>
                </TableRow>
              </TableBody>
            </Table>
            <StyledMidTitle>
              <Typography>
                <strong>{t("feetParametes").toUpperCase()}</strong>
              </Typography>
            </StyledMidTitle>
            <Table
              size="small"
              sx={{ minWidth: 400, marginBottom: 1 }}
              align="center"
            >
              <TableBody>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{personalData?.footLengthLeft}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">{t("footLength")}</NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>{personalData?.footLengthRight}</BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{personalData?.footWidthLeft}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">{t("footWidth")}</NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>{personalData?.footWidthRight}</BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{personalData?.mlaDepthLeft}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("medialArchDepth")}
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>{personalData?.mlaDepthRight}</BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{parseInt(visinasvodaYL)}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("medialArchHeight")}
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>{parseInt(visinasvodaYR)}</BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>
                      {personalData?.tendonPositionLeft ? (
                        <>
                          {personalData.tendonPositionLeft}{" "}
                          {personalData.legAlignmentLeft
                            ? `(${personalData.legAlignmentLeft})`
                            : ""}
                        </>
                      ) : (
                        "-"
                      )}
                    </BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("tendonBendingDegree")}
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>
                      {personalData?.tendonPositionRight ? (
                        <>
                          {personalData.tendonPositionRight}{" "}
                          {personalData.legAlignmentRight
                            ? `(${personalData.legAlignmentRight})`
                            : ""}
                        </>
                      ) : (
                        "-"
                      )}
                    </BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{Math.round(optimalnaDubinaLevo)}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("medialArchOptimalDepth")}
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>
                      {Math.round(optimalnaDubinaDesno)}
                    </BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{Math.round(visinaSvodaLevo)}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("medialArchOptimalHeight")}
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>{Math.round(visinaSvodaDesno)}</BoldWrapper>
                  </NoBorderCell>
                </TableRow>
              </TableBody>
            </Table>
            <StyledMidTitle>
              <Typography>
                <strong>{t("medialArchDisplacement")}</strong>
              </Typography>
            </StyledMidTitle>
            <Table
              size="small"
              sx={{ maxWidth: 400, marginBottom: 1 }}
              align="center"
            >
              <TableBody>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{fdxL}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("horizontalDisplacement")} FDx [mm]
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>{fdxD}</BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{fdyL}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("verticalDisplacement")} FDy [mm]
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>{fdyD}</BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{procenatlevo}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("displacementPoourcentage")} [%]
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>{procenatDesno}</BoldWrapper>
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>
                      {mlaEvelLeft?.stepen} ({mlaEvelLeft?.percentage}%)
                    </BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {t("displacementLevel")}
                  </NoBorderCell>
                  <NoBorderCell align="left">
                    <BoldWrapper>
                      {mlaEvelRight?.stepen} ({mlaEvelRight?.percentage}%)
                    </BoldWrapper>
                  </NoBorderCell>
                </TableRow>
              </TableBody>
            </Table>
            <StyledMidTitle>
              <Typography>
                <strong>EKOVEL STEP CORRECT</strong>
              </Typography>
            </StyledMidTitle>
            <Table
              size="small"
              sx={{ maxWidth: 400, marginBottom: 1 }}
              align="center"
            >
              <TableBody>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{t("euShoeSize")}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {personalData?.shoeSize}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{t("ekovelStepCorrectsize")}</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {personalData?.therapy?.escSize}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{t("initialHeight")} IY [mm]</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {personalData?.therapy?.iy}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>{t("optimalHeight")} OY [mm]</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {personalData?.therapy?.oy}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>
                      {t("adjustmentPeriod")} [{t("weeks")}]
                    </BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {personalData?.therapy?.ai}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="left">
                    <BoldWrapper>
                      {t("therapyDuration")} [{t("weeks")}]
                    </BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">{calculateTherapyDuration(personalData?.therapy?.oy, personalData?.therapy?.iy)}</NoBorderCell>
                </TableRow>
              </TableBody>
            </Table>
            <StyledMidTitle>
              <Typography>
                <strong>{t("controlExamSchedule").toUpperCase()}</strong>
              </Typography>
            </StyledMidTitle>
            <Table
              size="small"
              sx={{ maxWidth: 400, marginBottom: 1 }}
              align="center"
            >
              <TableBody>
                <TableRow>
                  <NoBorderCell align="right">
                    <BoldWrapper>1.</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                  {formatDates(check1)}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="right">
                    <BoldWrapper>2.</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {formatDates(check2)}
                  </NoBorderCell>
                </TableRow>
                <TableRow>
                  <NoBorderCell align="right">
                    <BoldWrapper>3.</BoldWrapper>
                  </NoBorderCell>
                  <NoBorderCell align="center">
                    {formatDates(check3)}
                  </NoBorderCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
          <DynamicImage imageName={rightImage} />
        </Box>
      </TableContainer>
    </Wrapper>
  );
};

export default Report;
